import { useState, useCallback, useEffect, useRef } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MoreVertical, RefreshCw } from 'lucide-react';
import { Sidebar } from './Sidebar';
import { WarehouseSelector } from '@/components/shared/WarehouseSelector';
import { useChatHeader } from '@/contexts/ChatHeaderContext';
import { useWarehouseId } from '@/contexts/WarehouseContext';
import { SUPPLY_REFRESH_SUGGESTED_TABLE_EVENT } from '@/utils/supplyTableCache';
import { SOP_FORCE_REFRESH_EVENT } from '@/utils/sopCache';
import { NETWORK_REPLENISHMENT_REFRESH_EVENT } from '@/utils/networkReplenishmentRefresh';
import { readSidebarCollapsed, persistSidebarCollapsed } from '@/utils/sidebarPrefs';

const ROUTE_HEADER: Record<string, { titleKey: string; subtitleKey?: string }> = {
  '/': { titleKey: 'app.name', subtitleKey: 'app.tagline' },
  '/home': { titleKey: 'home.title', subtitleKey: 'home.description' },
  '/history': { titleKey: 'app.historyTitle', subtitleKey: 'app.historySubtitle' },
  '/supply': { titleKey: 'supply.title', subtitleKey: 'supply.description' },
  '/supply/load-estimate': { titleKey: 'supply.loadEstimate.title', subtitleKey: 'supply.loadEstimate.description' },
  '/supply/rules': { titleKey: 'supply.rulesCatalog.title', subtitleKey: 'supply.rulesCatalog.description' },
  '/orders': { titleKey: 'orders.title', subtitleKey: 'orders.description' },
  '/business-rules': { titleKey: 'businessRules.title', subtitleKey: 'businessRules.description' },
  '/compliance-monitor': { titleKey: 'complianceMonitor.title', subtitleKey: 'complianceMonitor.subtitle' },
  '/order-tracking': { titleKey: 'orderTracking.title', subtitleKey: 'orderTracking.description' },
  '/forecast': { titleKey: 'forecastReview.title', subtitleKey: 'forecastReview.subtitle' },
  '/forecast/review': { titleKey: 'forecastReview.title', subtitleKey: 'forecastReview.subtitle' },
  '/forecast/data-validation': { titleKey: 'forecastReview.title', subtitleKey: 'forecastReview.subtitle' },
  '/demand-planning': { titleKey: 'sopModule.title', subtitleKey: 'sopModule.subtitle' },
  '/demand-planning/sop': { titleKey: 'sopModule.title', subtitleKey: 'sopModule.subtitle' },
  '/demand-planning/sop/plan': { titleKey: 'sopModule.title', subtitleKey: 'sopModule.subtitle' },
  '/demand-planning/sop/cycles': { titleKey: 'sopModule.title', subtitleKey: 'sopModule.subtitle' },
  '/demand-planning/sop/scenarios': { titleKey: 'sopModule.title', subtitleKey: 'sopModule.subtitle' },
  '/demand-planning/scenarios': { titleKey: 'sopModule.title', subtitleKey: 'sopModule.subtitle' },
  '/demand-planning/consensus': { titleKey: 'sopModule.title', subtitleKey: 'sopModule.subtitle' },
  '/demand-planning/planning-cycles': { titleKey: 'sopModule.title', subtitleKey: 'sopModule.subtitle' },
  '/demand-planning/forecast': { titleKey: 'forecastPage.title', subtitleKey: 'forecastPage.subtitle' },
  '/demand-planning/forecast/anomalies': {
    titleKey: 'forecastPage.anomaliesPage.title',
    subtitleKey: 'forecastPage.anomaliesPage.subtitle',
  },
  '/inventory-policy-control': { titleKey: 'inventoryPolicyControl.title', subtitleKey: 'inventoryPolicyControl.description' },
  '/inventory-policy-control/problems': { titleKey: 'inventoryPolicyControl.problemInsights.title', subtitleKey: 'inventoryPolicyControl.problemInsights.description' },
  '/inventory-policies': { titleKey: 'inventoryPolicies.title', subtitleKey: 'inventoryPolicies.description' },
  '/inventory-simulation': { titleKey: 'inventorySimulation.title', subtitleKey: 'inventorySimulation.description' },
  '/network-replenishment': { titleKey: 'networkReplenishment.title', subtitleKey: 'networkReplenishment.description' },
  '/network-replenishment/tray': { titleKey: 'networkReplenishment.title', subtitleKey: 'networkReplenishment.description' },
  '/network-replenishment/config': { titleKey: 'networkReplenishment.title', subtitleKey: 'networkReplenishment.description' },
  '/safety-stock': { titleKey: 'safetyStock.title', subtitleKey: 'safetyStock.description' },
  '/safety-stock/resumen': { titleKey: 'safetyStock.title', subtitleKey: 'safetyStock.description' },
  '/safety-stock/parametros': { titleKey: 'safetyStock.title', subtitleKey: 'safetyStock.description' },
  '/safety-stock/productos': { titleKey: 'safetyStock.title', subtitleKey: 'safetyStock.description' },
  '/safety-stock/recalculos': { titleKey: 'safetyStock.title', subtitleKey: 'safetyStock.description' },
  '/usage-monitoring': { titleKey: 'adoption.title', subtitleKey: 'adoption.description' },
  '/usage-monitoring/resumen': { titleKey: 'adoption.title', subtitleKey: 'adoption.description' },
  '/usage-monitoring/detalle': { titleKey: 'adoption.title', subtitleKey: 'adoption.description' },
  '/settings': { titleKey: 'settings.title', subtitleKey: 'settings.description' },
  '/settings/products': { titleKey: 'productMaster.title', subtitleKey: 'productMaster.description' },
  '/settings/equivalences': { titleKey: 'equivalences.title', subtitleKey: 'equivalences.description' },
  '/settings/logistics': { titleKey: 'logisticsCatalog.title', subtitleKey: 'logisticsCatalog.description' },
};

const isChatRoute = (path: string) => path === '/' || path.startsWith('/analysis/chat/');

export function MainLayout() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => readSidebarCollapsed());
  const contentScrollRef = useRef<HTMLDivElement | null>(null);

  const setSidebarCollapsedPersist = useCallback((update: boolean | ((prev: boolean) => boolean)) => {
    setSidebarCollapsed((prev) => {
      const next = typeof update === 'function' ? update(prev) : update;
      persistSidebarCollapsed(next);
      return next;
    });
  }, []);
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const warehouseId = useWarehouseId();
  const chatHeader = useChatHeader();
  const header =
    ROUTE_HEADER[pathname] ??
    (pathname.startsWith('/settings/products')
      ? ROUTE_HEADER['/settings/products']
      : pathname.startsWith('/settings/equivalences')
        ? ROUTE_HEADER['/settings/equivalences']
        : pathname.startsWith('/settings/logistics')
          ? ROUTE_HEADER['/settings/logistics']
          : pathname.startsWith('/demand-planning/sop')
      ? ROUTE_HEADER['/demand-planning/sop/plan']
      : pathname.startsWith('/demand-planning/forecast')
        ? ROUTE_HEADER['/demand-planning/forecast']
        : pathname.startsWith('/forecast')
          ? ROUTE_HEADER['/forecast']
          : pathname.startsWith('/network-replenishment')
            ? ROUTE_HEADER['/network-replenishment']
            : pathname.startsWith('/safety-stock')
              ? ROUTE_HEADER['/safety-stock']
              : pathname.startsWith('/usage-monitoring')
                ? ROUTE_HEADER['/usage-monitoring']
                : ROUTE_HEADER['/']);
  const showChatHeader = isChatRoute(pathname) && chatHeader?.isActive;
  const isForecastRoute = pathname === '/forecast' || pathname.startsWith('/forecast/');
  const isSopRoute = pathname.startsWith('/demand-planning/sop');
  const isNetworkReplenishmentRoute = pathname.startsWith('/network-replenishment');
  const isSafetyStockRoute = pathname.startsWith('/safety-stock');
  const isUsageMonitoringRoute = pathname.startsWith('/usage-monitoring');
  const isModuleShellRoute =
    isForecastRoute ||
    isSopRoute ||
    isNetworkReplenishmentRoute ||
    isSafetyStockRoute ||
    isUsageMonitoringRoute;
  const showWarehouseSelector =
    pathname === '/home' ||
    pathname === '/supply' ||
    pathname.startsWith('/supply/') ||
    pathname === '/orders' ||
    pathname === '/order-tracking' ||
    isForecastRoute ||
    pathname === '/inventory-policies' ||
    pathname === '/inventory-policy-control' ||
    pathname === '/inventory-simulation' ||
    isSafetyStockRoute;

  useEffect(() => {
    if (pathname === '/inventory-policy-control/problems') {
      contentScrollRef.current?.scrollTo({ top: 0, behavior: 'auto' });
    }
  }, [pathname]);

  return (
    <div className="flex h-full min-h-0 w-full overflow-hidden bg-stocker-sidebar">
      <Sidebar collapsed={sidebarCollapsed} onCollapsedChange={setSidebarCollapsedPersist} />
      <div className="flex-1 flex flex-col min-w-0 min-h-0 bg-white">
        <header className="flex-shrink-0 flex items-center justify-between gap-4 px-4 py-3 border-b border-gray-100 bg-white min-h-[52px]">
          <div className="min-w-0 flex-1 flex items-center gap-2">
            {showChatHeader && chatHeader ? (
              <>
                <input
                  type="text"
                  value={chatHeader.chatTitle}
                  onChange={(e) => chatHeader.setChatTitle(e.target.value)}
                  onBlur={() => {
                    const titleValue = chatHeader.chatTitle.trim() || t('chat.newChat');
                    chatHeader.setChatTitle(titleValue);
                    chatHeader.onTitleBlur(titleValue);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                  }}
                  className="flex-1 min-w-0 text-lg font-bold text-gray-900 bg-transparent border-none outline-none focus:ring-0 px-0 truncate placeholder:text-gray-400"
                  placeholder={t('chat.chatNamePlaceholder')}
                />
                <div className="relative flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => chatHeader.setMenuOpen(!chatHeader.menuOpen)}
                    className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                    aria-label={t('chat.moreOptions')}
                  >
                    <MoreVertical className="w-5 h-5" />
                  </button>
                  {chatHeader.menuOpen && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => chatHeader.setMenuOpen(false)} aria-hidden />
                      <div className="absolute right-0 top-full mt-1 py-1 rounded-lg bg-white border border-gray-200 shadow-lg z-20 min-w-[140px]">
                        <button
                          type="button"
                          onClick={() => { chatHeader.onSaveClick(); chatHeader.setMenuOpen(false); }}
                          className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                        >
                          {t('common.save')}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </>
            ) : pathname === '/' ? (
              sidebarCollapsed && (
                <h1 className="text-xl font-bold text-gray-900">
                  Stocker <sup className="text-sm font-bold">AI</sup>
                </h1>
              )
            ) : (
              <div className="min-w-0">
                <h1 className="text-xl font-bold text-gray-900 truncate">
                  {t(header.titleKey)}
                </h1>
                {header.subtitleKey && (
                  <p className="text-sm text-gray-500 truncate mt-0.5">
                    {t(header.subtitleKey)}
                  </p>
                )}
              </div>
            )}
          </div>
          {(showWarehouseSelector || isNetworkReplenishmentRoute) && (
            <div className="flex items-center gap-1.5 flex-shrink-0">
              {pathname === '/supply' && (
                <button
                  type="button"
                  onClick={() => window.dispatchEvent(new CustomEvent(SUPPLY_REFRESH_SUGGESTED_TABLE_EVENT))}
                  disabled={!warehouseId || typeof warehouseId !== 'number'}
                  className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white p-2 text-gray-700 shadow-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                  title={t('supply.refreshSuggestedTable')}
                  aria-label={t('supply.refreshSuggestedTable')}
                >
                  <RefreshCw className="h-4 w-4" />
                </button>
              )}
              {isForecastRoute && (
                <button
                  type="button"
                  onClick={() => window.dispatchEvent(new CustomEvent(SOP_FORCE_REFRESH_EVENT))}
                  disabled={!warehouseId || typeof warehouseId !== 'number'}
                  className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white p-2 text-gray-700 shadow-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                  title={t('sop.chart.refresh')}
                  aria-label={t('sop.chart.refresh')}
                >
                  <RefreshCw className="h-4 w-4" />
                </button>
              )}
              {isNetworkReplenishmentRoute && (
                <button
                  type="button"
                  onClick={() =>
                    window.dispatchEvent(new CustomEvent(NETWORK_REPLENISHMENT_REFRESH_EVENT))
                  }
                  className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white p-2 text-gray-700 shadow-sm hover:bg-gray-50"
                  title={t('networkReplenishment.refresh')}
                  aria-label={t('networkReplenishment.refresh')}
                >
                  <RefreshCw className="h-4 w-4" />
                </button>
              )}
              {showWarehouseSelector && (
                <WarehouseSelector
                  compact
                  className="max-w-[200px] flex-shrink-0"
                  allowSelectAll={false}
                />
              )}
            </div>
          )}
        </header>
        <div
          ref={contentScrollRef}
          className={
            isModuleShellRoute
              ? 'flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-white'
              : 'min-h-0 min-w-0 flex-1 overflow-y-auto overflow-x-hidden bg-white p-4 md:p-6'
          }
        >
          <Outlet context={{ sidebarCollapsed }} />
        </div>
      </div>
    </div>
  );
}
